const UUID_FILENAME = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const EXTENSION_BY_MIME: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'text/plain': 'txt',
  'text/csv': 'csv',
  'application/zip': 'zip',
  'application/json': 'json',
};

const TYPE_LABEL_BY_EXTENSION: Record<string, string> = {
  pdf: 'PDF',
  doc: 'Word',
  docx: 'Word',
  xls: 'Excel',
  xlsx: 'Excel',
  ppt: 'PowerPoint',
  pptx: 'PowerPoint',
  txt: 'Texto',
  csv: 'CSV',
  zip: 'ZIP',
  json: 'JSON',
};

export interface WhatsAppInboxDocumentPresentation {
  readonly title: string;
  readonly badge: string;
  readonly subtitle: string;
  readonly kind: string;
  readonly isPdf: boolean;
}

function extensionOf(name: string): string | null {
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot === name.length - 1) {
    return null;
  }
  const extension = name.slice(dot + 1).toLowerCase();
  if (!/^[a-z0-9]{1,8}$/.test(extension) || extension === 'undefined') {
    return null;
  }
  return extension;
}

export function presentWhatsAppInboxDocument(
  filename: string,
  mimetype: string,
): WhatsAppInboxDocumentPresentation {
  const stripped = filename.replace(/\.undefined$/i, '').trim();
  const extensionFromName = extensionOf(stripped);
  const extensionFromMime = EXTENSION_BY_MIME[mimetype.toLowerCase()] ?? null;
  const extension = extensionFromName ?? extensionFromMime;
  const badge = (extension ?? 'doc').slice(0, 4).toUpperCase();
  const typeLabel =
    extension !== null ? (TYPE_LABEL_BY_EXTENSION[extension] ?? badge) : 'Documento';
  const unnamed =
    stripped.length === 0 || UUID_FILENAME.test(stripped) || stripped.toLowerCase() === 'file';
  const title = unnamed ? (extension !== null ? `Documento.${extension}` : 'Documento') : stripped;

  return {
    title,
    badge,
    subtitle: typeLabel,
    kind: extension ?? 'file',
    isPdf: extension === 'pdf' || mimetype.toLowerCase() === 'application/pdf',
  };
}

export function formatWhatsAppInboxFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${String(bytes)} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${String(Math.max(1, Math.round(bytes / 1024)))} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function whatsAppInboxDocumentCaption(
  body: string | null,
  filename: string,
  title: string,
): string | null {
  if (body === null) {
    return null;
  }
  const trimmed = body.trim();
  if (trimmed.length === 0 || trimmed === filename.trim() || trimmed === title) {
    return null;
  }
  return trimmed;
}
